<template>
    <analytics-sidebar-wrapper @reset="emit('reset')">
        <analytics-blast-forecast-sender-sidebar
            v-if="FEATURES.contact_groups"
            :orgUnitId="props.orgUnitId"
            v-model:senderTypes="fetchParams.sender_types"
            v-model:orgUnitIdModel="fetchParams.org_unit_id"
            v-model:groupId="fetchParams.group_id"
        />

        <div class="col-span-4 lg:col-span-4">
            <analytics-sidebar-checkboxes v-model="fetchParams.statuses" :options="statusesOptions">
                <template v-slot:title>Status</template>
            </analytics-sidebar-checkboxes>
        </div>

        <div class="col-span-4 lg:col-span-4">
            <blast-forecast-recently-modified-filter
                v-model:startDate="fetchParams.modified_start_date"
                v-model:endDate="fetchParams.modified_end_date"
            />
        </div>

        <div class="col-span-4 lg:col-span-4">
            <analytics-sidebar-checkboxes v-model="fetchParams.audience_types" :options="audienceTypesOptionsList">
                <template v-slot:title> Audience Type </template>
            </analytics-sidebar-checkboxes>
        </div>

        <div class="col-span-4 lg:col-span-4">
            <analytics-sidebar-category v-model="fetchParams.category" />
        </div>

        <analytics-blast-forecast-locked-snippet-sidebar
            :orgUnitId="props.orgUnitId"
            v-model="fetchParams.snippet_id"
        />

        <div class="col-span-4 lg:col-span-4">
            <analytics-sidebar-box :canReset="contentFiltersChanged" @reset="resetContentFilters">
                <template v-slot:title> Content Filters </template>

                <div class="flex flex-col gap-3 text-sm">
                    <div data-test="emoji">
                        <div class="text-xs font-semibold mb-1.5">Emoji</div>
                        <content-filter-toggle v-model="fetchParams.has_emoji" />
                    </div>

                    <div data-test="urls">
                        <div class="text-xs font-semibold mb-1.5">URL</div>
                        <content-filter-toggle v-model="fetchParams.has_urls" />
                    </div>

                    <div data-test="variables">
                        <div class="text-xs font-semibold mb-1.5">Variables</div>
                        <content-filter-toggle v-model="fetchParams.has_variables" />
                    </div>

                    <div data-test="media">
                        <div class="text-xs font-semibold mb-1.5">Media</div>
                        <content-filter-toggle v-model="fetchParams.has_media" />
                    </div>
                </div>
            </analytics-sidebar-box>
        </div>

        <div class="col-span-4 lg:col-span-4">
            <analytics-sidebar-box :canReset="smsSegmentsChanged" @reset="resetSmsSegments">
                <template v-slot:title>SMS Segments</template>

                <div class="flex items-center">
                    <div class="grow">
                        <vx-input
                            :modelValue="fetchParams.min_sms_segments"
                            @input="handleSmsSegmentsInput($event, 'min_sms_segments')"
                            size="sm"
                            type="number"
                            :step="1"
                            :min="1"
                            :max="255"
                        ></vx-input>
                    </div>

                    <div class="h-px bg-slate-300 w-3"></div>

                    <div class="grow">
                        <vx-input
                            :modelValue="fetchParams.max_sms_segments"
                            @input="handleSmsSegmentsInput($event, 'max_sms_segments')"
                            size="sm"
                            type="number"
                            :step="1"
                            :min="1"
                            :max="255"
                        ></vx-input>
                    </div>
                </div>
            </analytics-sidebar-box>
        </div>

        <div class="col-span-4 lg:col-span-4" v-if="showEstimatedCost">
            <analytics-sidebar-box :canReset="estimatedCostChanged" @reset="resetEstimatedCost">
                <template v-slot:title>Estimated Cost</template>

                <div class="flex items-center">
                    <div class="grow">
                        <vx-input
                            :modelValue="minEstimatedCostDisplay"
                            @input="handleEstimatedCostInput($event, 'min_estimated_cost')"
                            size="sm"
                            type="text"
                            placeholder="$0"
                        ></vx-input>
                    </div>

                    <div class="h-px bg-slate-300 w-3"></div>

                    <div class="grow">
                        <vx-input
                            :modelValue="maxEstimatedCostDisplay"
                            @input="handleEstimatedCostInput($event, 'max_estimated_cost')"
                            size="sm"
                            type="text"
                            placeholder="$0"
                        ></vx-input>
                    </div>
                </div>
            </analytics-sidebar-box>
        </div>
    </analytics-sidebar-wrapper>
</template>

<script lang="ts" setup>
import AnalyticsSidebarWrapper from '../partial/sidebar/AnalyticsSidebarWrapper.vue';
import { computed, inject, watch, type PropType } from 'vue';
import AnalyticsSidebarCheckboxes from '../partial/sidebar/AnalyticsSidebarCheckboxes.vue';
import AnalyticsSidebarBox from '../partial/sidebar/AnalyticsSidebarBox.vue';
import AnalyticsSidebarCategory from '../partial/sidebar/AnalyticsSidebarCategory.vue';
import BlastForecastRecentlyModifiedFilter from './components/BlastForecastRecentlyModifiedFilter.vue';
import ContentFilterToggle from './components/ContentFilterToggle.vue';
import { useToasted } from '~/composables/useToasted';
import { faArrowRotateLeft } from '@fortawesome/pro-solid-svg-icons';
import { audienceTypes, CAMPAIGN_STATUSES, statuses } from '~/constants/campaign';
import AnalyticsBlastForecastSenderSidebar from './AnalyticsBlastForecastSenderSidebar.vue';
import AnalyticsBlastForecastLockedSnippetSidebar from './AnalyticsBlastForecastLockedSnippetSidebar.vue';
import type { GetBlastForecastRequest } from '~/services/blastForecast/blastForecastClient.types';
import type { Features } from '~/provides/features.types';
import { VxInput } from '@voxie/frontend-components';
import { formatNumber } from '~/utils/helpers';

const FEATURES = inject('FEATURES') as Features;

const props = defineProps({
    orgUnitId: {
        type: Number,
    },
    showEstimatedCost: {
        type: Boolean,
        required: true,
    },
});

const audienceTypesOptionsList = audienceTypes().map((audienceType) => ({
    value: audienceType.name,
    label: audienceType.label,
}));

const statusesOptions = [
    { value: statuses.DRAFT, label: CAMPAIGN_STATUSES.draft.label },
    { value: statuses.SCHEDULED, label: CAMPAIGN_STATUSES.scheduled.label },
    { value: statuses.PUBLISHED, label: CAMPAIGN_STATUSES.published.label },
    { value: statuses.COMPLETED, label: CAMPAIGN_STATUSES.completed.label },
];

const fetchParams = defineModel({
    type: Object as PropType<GetBlastForecastRequest>,
    required: true,
});

const emit = defineEmits(['reset']);

const minEstimatedCostDisplay = computed(() => {
    const cents = fetchParams.value.min_estimated_cost;
    if (cents === undefined || cents === null) {
        return '';
    }
    return `$${formatNumber(Math.floor(cents / 100))}`;
});

const maxEstimatedCostDisplay = computed(() => {
    const cents = fetchParams.value.max_estimated_cost;
    if (cents === undefined || cents === null) {
        return '';
    }
    return `$${formatNumber(Math.floor(cents / 100))}`;
});

const contentFiltersChanged = computed(() => {
    return (
        fetchParams.value.has_emoji !== undefined ||
        fetchParams.value.has_urls !== undefined ||
        fetchParams.value.has_variables !== undefined ||
        fetchParams.value.has_media !== undefined
    );
});

const smsSegmentsChanged = computed(() => {
    return fetchParams.value.min_sms_segments !== undefined || fetchParams.value.max_sms_segments !== undefined;
});

const estimatedCostChanged = computed(() => {
    return fetchParams.value.min_estimated_cost !== undefined || fetchParams.value.max_estimated_cost !== undefined;
});

const resetContentFilters = () => {
    fetchParams.value.has_emoji = undefined;
    fetchParams.value.has_urls = undefined;
    fetchParams.value.has_variables = undefined;
    fetchParams.value.has_media = undefined;
};

const resetSmsSegments = () => {
    fetchParams.value.min_sms_segments = undefined;
    fetchParams.value.max_sms_segments = undefined;
};

const handleSmsSegmentsInput = (event: Event, field: 'min_sms_segments' | 'max_sms_segments') => {
    const input = event.target as HTMLInputElement;
    const value = input.value.replace(/[^0-9]/g, '');

    if (value === '') {
        fetchParams.value[field] = undefined;
        input.value = '';
        return;
    }

    let numValue = parseInt(value, 10);
    if (numValue < 1) {
        numValue = 1;
    } else if (numValue > 255) {
        numValue = 255;
    }

    fetchParams.value[field] = numValue;
    input.value = String(numValue);
};

const resetEstimatedCost = () => {
    fetchParams.value.min_estimated_cost = undefined;
    fetchParams.value.max_estimated_cost = undefined;
};

const toasted = useToasted();

watch(
    [() => fetchParams.value.modified_start_date, () => fetchParams.value.modified_end_date],
    ([startDate, endDate]) => {
        if (
            (undefined !== startDate || undefined !== endDate) &&
            fetchParams.value.statuses.includes(statuses.PUBLISHED)
        ) {
            fetchParams.value.statuses = fetchParams.value.statuses.filter((s) => s !== statuses.PUBLISHED);
            const toastId = toasted.info('Live status filter was automatically removed', {
                action: {
                    label: 'Undo',
                    icon: faArrowRotateLeft,
                    onClick: () => {
                        if (!fetchParams.value.statuses.includes(statuses.PUBLISHED)) {
                            fetchParams.value.statuses = [...fetchParams.value.statuses, statuses.PUBLISHED];
                        }
                        toasted.dismiss(toastId);
                    },
                },
            });
        }
    }
);

const handleEstimatedCostInput = (event: Event, field: 'min_estimated_cost' | 'max_estimated_cost') => {
    const input = event.target as HTMLInputElement;
    const value = input.value.replace(/[^0-9]/g, '');

    if (value === '') {
        fetchParams.value[field] = undefined;
        input.value = '';
        return;
    }

    const dollars = parseInt(value, 10);
    fetchParams.value[field] = dollars * 100;
    input.value = `$${formatNumber(dollars)}`;
};
</script>
